import { useState } from 'react';
import {
  AppBar as AppBarMui,
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  ExitToApp as ExitToAppIcon,
  KeyboardArrowDown,
  ArrowDropDown,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/user.context';
import { CustomTheme } from '../../contexts/theme.context';
import { PopUpMenu } from './PopUpMenu';
import { BugReport } from './BugReport';
import { Permission } from '../../../../types/constants/permission';

export const AppBar = () => {
  const theme = useTheme<CustomTheme>();
  const navigate = useNavigate();
  const [user] = useUser();
  const [reportBugOpen, setReportBugOpen] = useState(false);

  const {
    VITE_CIVIL3D_DOWNLOAD_URL: civil3DDownloadUrl,
    VITE_GIS_DOWNLOAD_URL: gisDownloadUrl,
    VITE_MICROSTATION_DOWNLOAD_URL: microstationDownloadUrl,
    VITE_TBC_DOWNLOAD_URL: tbcDownloadUrl,
  } = import.meta.env;

  const handleLogout = () => {
    window.localStorage.removeItem('accessToken');
    navigate(0);
  };

  const handleGenerateGIS = () => {
    if (user?.hasPermission(Permission.ARCGIS_SCHEMAS)) {
      navigate('/export-arcgis-schema');
      return;
    }

    window.location.href = gisDownloadUrl;
  };

  const renderProfilePopupMenu = () => (
    <PopUpMenu
      menuText={`${user?.firstName} ${user?.lastName}`}
      buttonProps={{
        sx: {
          color: '#fff',
          border: `1px solid ${theme.palette.primary.dark}`,
          padding: '6px 0',
          borderRadius: '6px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          ':hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
        },
        endIcon: <ArrowDropDown sx={{ pr: 1 }} />,
        startIcon: (
          <>
            <Box sx={{ pl: '10px' }}>
              <Avatar sx={{ backgroundColor: theme.palette.primary.dark }}>
                {`${user?.firstName?.[0]}${user?.lastName?.[0]}`}
              </Avatar>
            </Box>
          </>
        ),
      }}
    >
      <MenuItem onClick={() => navigate('/change-password')}>
        <ListItemIcon>
          <VpnKeyIcon />
        </ListItemIcon>
        <ListItemText primary="Change Password" />
      </MenuItem>
      <Divider />
      <MenuItem sx={{ color: theme.palette.error.main }} onClick={() => handleLogout()}>
        <ListItemIcon>
          <ExitToAppIcon sx={{ color: theme.palette.error.main }} />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </MenuItem>
    </PopUpMenu>
  );

  const mainMenu = (
    <>
      <Button style={{ color: '#fff' }} onClick={() => navigate('/')}>
        Home
      </Button>

      <PopUpMenu menuText="Information" buttonProps={{ sx: { color: '#fff' }, endIcon: <KeyboardArrowDown /> }}>
        <MenuItem onClick={() => navigate('/contact')}>Contact</MenuItem>
        <MenuItem
          onClick={() => {
            window.location.href = '/api-docs';
          }}
        >
          API documentation
        </MenuItem>
        <MenuItem onClick={() => setReportBugOpen(true)}>Report a Bug</MenuItem>
      </PopUpMenu>

      {user?.hasPermission(Permission.EXPORT) ? (
        <PopUpMenu menuText="Actions" buttonProps={{ sx: { color: '#fff' }, endIcon: <KeyboardArrowDown /> }}>
          <MenuItem
            onClick={() => {
              window.location.href = civil3DDownloadUrl;
            }}
          >
            Generate Civil 3D
          </MenuItem>
          <MenuItem onClick={handleGenerateGIS}>Generate GIS</MenuItem>
          <MenuItem
            onClick={() => {
              window.location.href = microstationDownloadUrl;
            }}
          >
            Generate Microstation
          </MenuItem>
          <MenuItem
            onClick={() => {
              window.location.href = tbcDownloadUrl;
            }}
          >
            Generate TBC
          </MenuItem>
          <MenuItem onClick={() => navigate('/export-fxl')}>Generate FXL</MenuItem>
        </PopUpMenu>
      ) : null}

      {user?.hasPermission(Permission.STYLE_EDITOR) ? (
        <PopUpMenu menuText="Style Editor" buttonProps={{ sx: { color: '#fff' }, endIcon: <KeyboardArrowDown /> }}>
          {user?.hasPermission(Permission.ARCGIS_CIVIL3D_SETTINGS) && (
            <MenuItem onClick={() => navigate('/arcgis-civil3d-data-editor')}>ArcGIS - Civil 3D Data Editor</MenuItem>
          )}
          <MenuItem onClick={() => navigate('/cad-blocks')}>CAD Blocks</MenuItem>
          <MenuItem onClick={() => navigate('/cad-layers')}>CAD Layers</MenuItem>
          <MenuItem onClick={() => navigate('/civil-3d-point-label-styles')}>Civil 3D Point Label Styles</MenuItem>
          <MenuItem onClick={() => navigate('/civil-3d-point-styles')}>Civil 3D Point Styles</MenuItem>
          <MenuItem onClick={() => navigate('/trimble-layers')}>Trimble Layers</MenuItem>
          <MenuItem onClick={() => navigate('/symbols')}>Symbols & Lines</MenuItem>
        </PopUpMenu>
      ) : null}

      <PopUpMenu menuText="System" buttonProps={{ sx: { color: '#fff' }, endIcon: <KeyboardArrowDown /> }}>
        {user?.hasPermission(Permission.AASHTOWARE) ? (
          <MenuItem onClick={() => navigate('/aashtoware')}>Aashtoware</MenuItem>
        ) : null}
        {user?.hasPermission(Permission.CONTRACTS) ? (
          <MenuItem onClick={() => navigate('/contracts')}>Contracts</MenuItem>
        ) : null}
        {user?.hasPermission(Permission.EVENT_LOG) ? (
          <MenuItem onClick={() => navigate('/event-logs')}>Event Logs</MenuItem>
        ) : null}
        {user?.hasPermission(Permission.CIVIL_3D_TEMPLATES) ? (
          <>
            <MenuItem onClick={() => navigate('/create-civil-3d-template')}>Civil 3D - Generate Template</MenuItem>
            <MenuItem onClick={() => navigate('/templates')}>Civil 3D - Template Manager</MenuItem>
          </>
        ) : null}
        {user?.hasPermission(Permission.DOMAINS) ? (
          <MenuItem onClick={() => navigate('/domains')}>Domains</MenuItem>
        ) : null}
        {user?.hasPermission(Permission.FEATURE_TYPES) ? (
          <MenuItem onClick={() => navigate('/feature-types')}>Feature Types</MenuItem>
        ) : null}
        {user?.hasPermission(Permission.USERS) ? <MenuItem onClick={() => navigate('/users')}>Users</MenuItem> : null}
        {user?.hasPermission(Permission.ROLES) ? <MenuItem onClick={() => navigate('/roles')}>Roles</MenuItem> : null}
        {user?.hasPermission(Permission.SETTINGS) ? (
          <MenuItem onClick={() => navigate('/settings')}>Settings</MenuItem>
        ) : null}

        {user?.hasPermission(Permission.SYNC) ? <MenuItem onClick={() => navigate('/sync')}>Sync</MenuItem> : null}
      </PopUpMenu>
    </>
  );

  return (
    <AppBarMui position="static" sx={{ borderBottom: `1px solid ${theme.palette.primary.dark}` }}>
      <BugReport open={reportBugOpen} onClose={() => setReportBugOpen(false)} />
      <Box display="flex" flexDirection="column">
        <Toolbar style={{ height: '68px', marginTop: '10px' }}>
          <Hidden smUp>
            <img src={theme.companyLogo} style={{ height: '40px' }} />
            <Typography
              variant="h4"
              noWrap
              style={{
                fontFamily: 'Roboto, sans-serif',
                margin: '0 10px',
              }}
            >
              <b>{theme.companyName}</b>
            </Typography>
          </Hidden>
          <Hidden smDown>
            <img src={theme.companyLogo} style={{ height: '60px' }} />
            <Typography
              variant="h4"
              noWrap
              style={{
                fontFamily: 'Roboto, sans-serif',
                margin: '0 10px',
              }}
            >
              <b>{theme.companyName}</b>
            </Typography>
          </Hidden>
          <div style={{ flexGrow: 1 }} />
          <div style={{ display: 'flex' }}>
            <Box display="flex" flexDirection="column">
              {renderProfilePopupMenu()}
            </Box>
          </div>
        </Toolbar>
        <Toolbar>
          {mainMenu}
          <div style={{ flexGrow: 1 }} />
        </Toolbar>
      </Box>
    </AppBarMui>
  );
};
